import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Database, AlertTriangle, ChevronDown, CheckCircle2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { targetAPI } from '../../lib/api';
import { useRBAC } from '../../lib/RBACContext';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const formatRelative = (value) => {
  if (!value) return 'Unknown';
  const dt = new Date(value);
  const diffMs = Date.now() - dt.getTime();
  const diffMin = Math.max(0, Math.round(diffMs / 60000));
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  return `${diffDay}d ago`;
};

const freshnessTone = (isOverdue, hasSync) => {
  if (!hasSync) return 'bg-slate-100 text-slate-700 border-slate-200';
  if (isOverdue) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-emerald-100 text-emerald-700 border-emerald-200';
};

export function DataFreshnessBar({ entityIds = [], actionEntityIds = [], title = 'Data Freshness', dataTestId = 'data-freshness-bar' }) {
  const [entities, setEntities] = useState([]);
  const [syncing, setSyncing] = useState({});
  const { permissions = [], hasPermission } = useRBAC();

  const refreshOverview = () => {
    targetAPI.getHubOverview().then((response) => {
      setEntities(response.data?.entities || []);
    }).catch(() => {
      setEntities([]);
    });
  };

  useEffect(() => {
    let mounted = true;
    targetAPI.getHubOverview().then((response) => {
      if (!mounted) return;
      setEntities(response.data?.entities || []);
    }).catch(() => {
      if (!mounted) return;
      setEntities([]);
    });
    return () => { mounted = false; };
  }, []);

  const canSync = hasPermission?.('system_admin') || permissions.includes('admin:*') || permissions.includes('system_admin');

  const handleSync = async (entityId) => {
    setSyncing((prev) => ({ ...prev, [entityId]: true }));
    try {
      await targetAPI.triggerSync(entityId);
      toast.success(`Resync queued for ${entityId.replace(/_/g, ' ')}`);
      window.setTimeout(refreshOverview, 2500);
    } catch (error) {
      console.error(error);
      toast.error(`Failed to queue resync for ${entityId.replace(/_/g, ' ')}`);
    } finally {
      setSyncing((prev) => ({ ...prev, [entityId]: false }));
    }
  };

  const visibleEntities = useMemo(() => {
    const wanted = new Set(entityIds);
    return entities.filter((entity) => wanted.has(entity.id));
  }, [entities, entityIds]);

  const freshestSync = useMemo(() => {
    const withSync = visibleEntities.filter((entity) => entity.last_sync);
    if (withSync.length === 0) return null;
    return withSync.sort((a, b) => new Date(b.last_sync) - new Date(a.last_sync))[0];
  }, [visibleEntities]);

  const hasAnyOverdue = visibleEntities.some((entity) => entity.is_overdue);
  const summaryTone = hasAnyOverdue ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200';

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-white/80 backdrop-blur px-3 py-2" data-testid={dataTestId}>
      <div className="flex items-center gap-2 min-w-0">
        <div className={`w-2.5 h-2.5 rounded-full ${hasAnyOverdue ? 'bg-amber-500' : 'bg-emerald-500'} ${!hasAnyOverdue ? 'animate-pulse' : ''}`} />
        <div className="min-w-0">
          <p className="text-xs font-semibold text-slate-800 leading-none">{title}</p>
          <p className="text-[11px] text-slate-500 mt-1 leading-none">
            {freshestSync ? `Last synced ${formatRelative(freshestSync.last_sync)}` : 'Freshness unavailable'}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="outline" className={`${summaryTone} text-[10px] h-6`}>
          {hasAnyOverdue ? <AlertTriangle className="h-3 w-3 mr-1" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
          {hasAnyOverdue ? 'Needs attention' : 'Fresh'}
        </Badge>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 text-[10px] px-2.5" data-testid={`${dataTestId}-details-button`}>
              <Database className="h-3 w-3 mr-1" /> Details <ChevronDown className="h-3 w-3 ml-1" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[360px] rounded-2xl border bg-white/90 backdrop-blur p-3 space-y-3" align="end">
            <div>
              <p className="text-xs font-semibold text-slate-800">Odoo Freshness Details</p>
              <p className="text-[11px] text-slate-500 mt-1">Latest sync state for the datasets powering this page.</p>
            </div>
            <div className="space-y-2">
              {visibleEntities.length > 0 ? visibleEntities.map((entity) => (
                <div key={entity.id} className="flex items-center justify-between gap-2 rounded-xl border bg-slate-50 px-3 py-2">
                  <div>
                    <p className="text-xs font-medium text-slate-800">{entity.label}</p>
                    <p className="text-[11px] text-slate-500">{formatRelative(entity.last_sync)} • {entity.record_count} records • {entity.schedule}</p>
                  </div>
                  <Badge variant="outline" className={`${freshnessTone(entity.is_overdue, !!entity.last_sync)} text-[10px] h-5`}>
                    {entity.is_overdue ? 'Stale' : 'Synced'}
                  </Badge>
                </div>
              )) : (
                <p className="text-xs text-slate-500">No sync state available for this module yet.</p>
              )}
            </div>
            {canSync && actionEntityIds.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid={`${dataTestId}-sync-actions`}>
                {actionEntityIds.map((entityId) => (
                  <Button key={entityId} variant="outline" size="sm" className="h-7 text-[10px] px-2.5" disabled={!!syncing[entityId]} onClick={() => handleSync(entityId)} data-testid={`${dataTestId}-sync-${entityId}`}>
                    <RefreshCw className={`h-3 w-3 mr-1 ${syncing[entityId] ? 'animate-spin' : ''}`} />
                    Resync {entityId.replace(/_/g, ' ')}
                  </Button>
                ))}
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
